import { useState, useRef, useEffect } from 'react';
import { MessageCircle, ChevronDown, Loader2, ExternalLink, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Per-order button that lets an admin send a WhatsApp notification to the
 * customer via a wa.me deep link. Semi-automatic: opens the admin's own
 * WhatsApp with a pre-filled localized message. See backend
 * GET /api/admin/orders/{id}/whatsapp-notify?event={order_created|ready|shipped}.
 */
const EVENT_LABELS = {
  order_created: 'Accusé de réception',
  ready: 'Prête au paiement',
  shipped: 'Envoyée + suivi',
};

// Show events relevant to the current order status. All 3 remain accessible
// so the admin can resend an acknowledgment or a payment reminder at any time.
const eventsForStatus = (status) => {
  const base = ['order_created', 'ready'];
  if (status === 'shipped' || status === 'delivered') base.push('shipped');
  return base;
};

export const WhatsAppNotifyButton = ({ orderId, status, customerPhone }) => {
  const [open, setOpen] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(null);
  const [preview, setPreview] = useState(null); // { message, whatsapp_url, event }
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const fetchTemplate = async (event) => {
    setLoadingEvent(event);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/orders/${orderId}/whatsapp-notify`,
        { params: { event } },
      );
      setPreview({ ...res.data, event });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la génération du message');
    } finally {
      setLoadingEvent(null);
    }
  };

  const copyMessage = async () => {
    if (!preview?.message) return;
    try {
      await navigator.clipboard.writeText(preview.message);
      toast.success('Message copié');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const events = eventsForStatus(status);
  const disabled = !customerPhone;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={disabled ? 'Aucun numéro de téléphone client' : 'Prévenir le client via WhatsApp'}
        className="flex items-center gap-1 text-xs border border-white/10 text-zinc-400 hover:text-[#25D366] hover:border-[color:rgb(37_211_102/0.40)] px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`wa-notify-${orderId}`}
      >
        <MessageCircle className="w-3 h-3" />
        WhatsApp
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 z-30 w-72 bg-[color:var(--color-surface)] border border-white/10 shadow-2xl"
          data-testid={`wa-notify-menu-${orderId}`}
        >
          {!preview ? (
            <div className="p-2">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider px-2 py-1.5">
                Notifier le client :
              </p>
              {events.map((ev) => (
                <button
                  key={ev}
                  type="button"
                  onClick={() => fetchTemplate(ev)}
                  disabled={loadingEvent !== null}
                  className="w-full flex items-center justify-between gap-2 text-left text-sm text-zinc-300 hover:bg-[color:rgb(37_211_102/0.10)] hover:text-white px-2 py-2 transition-colors disabled:opacity-50"
                  data-testid={`wa-notify-${orderId}-${ev}`}
                >
                  <span>{EVENT_LABELS[ev]}</span>
                  {loadingEvent === ev && <Loader2 className="w-3 h-3 animate-spin" />}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-3 space-y-3">
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">
                  Aperçu ({preview.language?.toUpperCase()} — {EVENT_LABELS[preview.event]})
                </p>
                <p
                  className="text-zinc-300 text-xs whitespace-pre-line max-h-40 overflow-y-auto border border-white/5 p-2 bg-black/40"
                  data-testid={`wa-notify-preview-${orderId}`}
                >
                  {preview.message}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href={preview.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 text-xs bg-[#25D366] text-black hover:bg-[#20BD5A] px-3 py-2 transition-colors"
                  data-testid={`wa-notify-send-${orderId}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  Ouvrir dans WhatsApp
                </a>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={copyMessage}
                    className="flex-1 flex items-center justify-center gap-1 text-xs border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-3 py-1.5 transition-colors"
                    data-testid={`wa-notify-copy-${orderId}`}
                  >
                    <Copy className="w-3 h-3" /> Copier
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreview(null)}
                    className="flex-1 text-xs border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-3 py-1.5 transition-colors"
                    data-testid={`wa-notify-back-${orderId}`}
                  >
                    Retour
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppNotifyButton;
